import React, { useState, useRef } from 'react';
import { Upload, FileText, Sparkles, Loader2, ChevronDown, ChevronRight, FolderOpen, Package, Trash2, Pencil, Check } from 'lucide-react';
import Modal from '../../../components/Modal';
import api from '../../../services/api';

// ==================== TREE PREVIEW ====================

function PreviewNode({ node, depth, onRemove, onEditName }) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(node.name);
  const isGroup = node.item_type === 'group';
  const hasChildren = node.children && node.children.length > 0;
  const fmt = (p) => Number(p || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="flex items-center gap-1 py-1 px-2 hover:bg-gray-50 rounded group"
        style={{ paddingLeft: `${depth * 18 + 4}px` }}>
        {isGroup && hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5">
            {expanded ? <ChevronDown size={12} className="text-gray-400" /> : <ChevronRight size={12} className="text-gray-400" />}
          </button>
        ) : <span className="w-4" />}
        {isGroup ? <FolderOpen size={13} className="text-amber-500" /> : <Package size={13} className="text-gray-400" />}
        {editing ? (
          <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus
            className="flex-1 px-1 py-0.5 text-xs border border-blue-400 rounded"
            onBlur={() => { onEditName(node, name); setEditing(false); }}
            onKeyDown={e => { if (e.key === 'Enter') { onEditName(node, name); setEditing(false); } }} />
        ) : (
          <span className={`flex-1 text-xs truncate ${isGroup ? 'font-medium text-gray-800' : 'text-gray-700'}`}
            onDoubleClick={() => setEditing(true)}>
            {node.name}
          </span>
        )}
        {!isGroup && node.unit && <span className="text-[10px] text-gray-400 mr-1">{node.unit}</span>}
        {!isGroup && node.price_per_unit > 0 && (
          <span className="text-xs font-medium text-gray-700 w-20 text-right">{fmt(node.price_per_unit)}</span>
        )}
        <button onClick={() => onRemove(node)} className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded">
          <Trash2 size={11} className="text-red-400" />
        </button>
      </div>
      {isGroup && expanded && hasChildren && node.children.map((child, i) => (
        <PreviewNode key={i} node={child} depth={depth + 1} onRemove={onRemove} onEditName={onEditName} />
      ))}
    </div>
  );
}

// ==================== IMPORT DIALOG ====================

export default function ImportDialog({ isOpen, onClose, onCreated, toast }) {
  const [step, setStep] = useState('input'); // input | preview
  const [mode, setMode] = useState('text'); // text | pdf | image
  const [text, setText] = useState('');
  const [rawPdfText, setRawPdfText] = useState('');
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('PLN');
  const fileRef = useRef(null);

  const reset = () => {
    setStep('input'); setText(''); setRawPdfText(''); setTree([]); setFlat([]); setStats(null); setName(''); setLoading(false);
  };

  const handleParseText = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/pricelists/import/parse-text', { text });
      setTree(res.data.tree);
      setFlat(res.data.flat);
      setStats(res.data.stats);
      setStep('preview');
      if (!name) setName('Import ' + new Date().toLocaleDateString('pl-PL'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd parsowania');
    } finally { setLoading(false); }
  };

  const handleParsePdf = async (file) => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/pricelists/import/parse-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTree(res.data.tree);
      setFlat(res.data.flat);
      setStats(res.data.stats);
      setRawPdfText(res.data.raw_text || '');
      setStep('preview');
      if (!name) setName(file.name.replace(/\.pdf$/i, ''));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd odczytu PDF');
    } finally { setLoading(false); }
  };

  const handleParseImage = async (file) => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/pricelists/import/parse-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTree(res.data.tree);
      setFlat(res.data.flat);
      setStats(res.data.stats);
      setRawPdfText(res.data.raw_text || '');
      setStep('preview');
      if (!name) setName(file.name.replace(/\.\w+$/, ''));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Blad OCR');
    } finally { setLoading(false); }
  };

  const handleRemoveNode = (nodeToRemove) => {
    const removeFromTree = (nodes) => nodes
      .filter(n => n !== nodeToRemove)
      .map(n => ({ ...n, children: n.children ? removeFromTree(n.children) : [] }));
    setTree(removeFromTree(tree));
    // Rebuild flat
    const rebuildFlat = (nodes, parentId, order) => {
      const items = [];
      for (const n of nodes) {
        const item = { ...n, parent_id: parentId, display_order: order.n++, _tempId: n._tempId || Math.random().toString(36).substr(2, 9) };
        items.push(item);
        if (n.children) items.push(...rebuildFlat(n.children, item._tempId, order));
      }
      return items;
    };
    setFlat(rebuildFlat(removeFromTree(tree), null, { n: 0 }));
  };

  const handleEditName = (node, newName) => {
    const editInTree = (nodes) => nodes.map(n =>
      n === node ? { ...n, name: newName } : { ...n, children: n.children ? editInTree(n.children) : [] }
    );
    setTree(editInTree(tree));
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Podaj nazwę cennika'); return; }
    setLoading(true);
    try {
      // Rebuild flat from current tree state
      const rebuildFlat = (nodes, parentId, order) => {
        const items = [];
        for (const n of nodes) {
          const price = parseFloat(n.price_per_unit) || 0;
          const unitVal = n.unit || 'szt';
          // Generate spreadsheet with price data for items
          const spreadsheet = (n.item_type === 'item' && price > 0)
            ? {
                columns: [
                  { key: 'A', label: 'Opis', width: 180 },
                  { key: 'B', label: 'Ilość', width: 80 },
                  { key: 'C', label: 'J.m.', width: 60 },
                  { key: 'D', label: 'Cena/j.m.', width: 100 },
                  { key: 'E', label: 'Wartość', width: 100 },
                ],
                rows: [{ A: "'" + n.name, B: '1', C: "'" + unitVal, D: String(price), E: '=B*D' }],
                exports: {},
              }
            : { columns: [], rows: [], exports: {} };
          const item = {
            parent_id: parentId, item_type: n.item_type || 'item', name: n.name,
            description: n.description || '', unit: unitVal,
            price_per_unit: price, display_order: order.n++,
            spreadsheet,
            _tempId: n._tempId || Math.random().toString(36).substr(2, 9),
          };
          items.push(item);
          if (n.children && n.children.length > 0) items.push(...rebuildFlat(n.children, item._tempId, order));
        }
        return items;
      };
      const finalFlat = rebuildFlat(tree, null, { n: 0 });

      const res = await api.post('/pricelists/import/create', { name, currency, items: finalFlat });
      toast.success(`Cennik "${name}" utworzony (${res.data.items_created} pozycji)`);
      reset();
      onClose();
      if (onCreated) onCreated(res.data.pricelist);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd tworzenia');
    } finally { setLoading(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }}
      title="Import cennika" maxWidth="max-w-4xl">
      {step === 'input' && (
        <div className="space-y-4">
          {/* Mode tabs */}
          <div className="flex gap-1 border-b">
            <button onClick={() => setMode('text')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${mode === 'text' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500'}`}>
              <FileText size={14} className="inline mr-1" /> Wklej tekst
            </button>
            <button onClick={() => setMode('pdf')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${mode === 'pdf' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500'}`}>
              <Upload size={14} className="inline mr-1" /> Plik PDF
            </button>
            <button onClick={() => setMode('image')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${mode === 'image' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500'}`}>
              <Sparkles size={14} className="inline mr-1" /> Zdjecie / Skan (AI OCR)
            </button>
          </div>

          {mode === 'text' && (
            <>
              <p className="text-sm text-gray-500">
                Wklej tekst cennika — system rozpozna strukturę (grupy, pozycje, ceny, jednostki).
              </p>
              <textarea value={text} onChange={e => setText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed"
                rows={14} placeholder={"1. MATERIAŁY BUDOWLANE\n  Blacha ocynk 0.5mm    m²    45.00 zł\n  Blacha ocynk 0.7mm    m²    62.00 zł\n\n2. USŁUGI MONTAŻOWE\n  Montaż pokrycia        m²    35.00 zł\n  Transport              km    4.50 zł"} />
              <div className="flex justify-end gap-2">
                <button onClick={() => { reset(); onClose(); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
                <button onClick={handleParseText} disabled={!text.trim() || loading}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg text-sm">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Analizuj tekst
                </button>
              </div>
            </>
          )}

          {mode === 'pdf' && (
            <>
              <p className="text-sm text-gray-500">
                Załaduj plik PDF — system wyodrębni tekst i zaproponuje strukturę cennika.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-400 transition cursor-pointer"
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-emerald-400', 'bg-emerald-50'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('border-emerald-400', 'bg-emerald-50'); }}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-emerald-400', 'bg-emerald-50'); const f = e.dataTransfer.files[0]; if (f) handleParsePdf(f); }}>
                <Upload size={32} className="mx-auto mb-3 text-gray-400" />
                <p className="text-sm text-gray-600">Kliknij lub przeciągnij plik PDF</p>
                <p className="text-xs text-gray-400 mt-1">Maksymalnie 10 MB</p>
                {loading && <Loader2 size={20} className="animate-spin mx-auto mt-3 text-emerald-600" />}
              </div>
              <input ref={fileRef} type="file" accept=".pdf" className="hidden"
                onChange={e => { if (e.target.files[0]) handleParsePdf(e.target.files[0]); }} />
            </>
          )}

          {mode === 'image' && (
            <>
              <p className="text-sm text-gray-500">
                Zrob zdjecie cennika lub zaladuj skan — AI odczyta tekst i zaproponuje strukture.
                Obsluguje: zdjecia, skany, screenshoty tabel.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-400 transition cursor-pointer"
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-emerald-400', 'bg-emerald-50'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('border-emerald-400', 'bg-emerald-50'); }}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-emerald-400', 'bg-emerald-50'); const f = e.dataTransfer.files[0]; if (f) handleParseImage(f); }}>
                <Sparkles size={32} className="mx-auto mb-3 text-purple-400" />
                <p className="text-sm text-gray-600">Kliknij lub przeciagnij zdjecie/skan</p>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP — max 10 MB. Wymaga klucza AI.</p>
                {loading && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-purple-600">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-sm">AI analizuje obraz...</span>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={e => { if (e.target.files[0]) handleParseImage(e.target.files[0]); }} />
            </>
          )}
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4 text-xs">
            <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded">{stats?.groups || 0} grup</span>
            <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded">{stats?.items || 0} pozycji</span>
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{stats?.with_price || 0} z ceną</span>
            {stats?.pages && <span className="px-2 py-1 bg-gray-50 text-gray-600 rounded">{stats.pages} stron PDF</span>}
          </div>

          {/* Name + currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Nazwa cennika *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Waluta</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="PLN">PLN</option><option value="EUR">EUR</option><option value="USD">USD</option>
              </select>
            </div>
          </div>

          {/* Preview tree */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Podgląd struktury <span className="text-gray-400">(2x klik = edytuj nazwę, kliknij 🗑 aby usunąć)</span>
            </label>
            <div className="border border-gray-200 rounded-lg max-h-72 overflow-auto p-1 bg-white">
              {tree.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-xs">Nie rozpoznano struktury</div>
              ) : (
                tree.map((node, i) => (
                  <PreviewNode key={i} node={node} depth={0} onRemove={handleRemoveNode} onEditName={handleEditName} />
                ))
              )}
            </div>
          </div>

          {/* Raw PDF text toggle */}
          {rawPdfText && (
            <details className="text-xs">
              <summary className="text-gray-400 cursor-pointer hover:text-gray-600">Pokaż surowy tekst z PDF</summary>
              <pre className="mt-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-auto text-[10px] text-gray-600 whitespace-pre-wrap">{rawPdfText}</pre>
            </details>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setStep('input')} className="text-sm text-gray-500 hover:underline">
              ← Wróć do edycji tekstu
            </button>
            <div className="flex gap-2">
              <button onClick={() => { reset(); onClose(); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
              <button onClick={handleCreate} disabled={loading || !name.trim() || tree.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg text-sm">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Utwórz cennik ({flat.length} pozycji)
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
